import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect, useCallback } from 'react';
import VideoGrid from './components/VideoGrid';
import ChatPanel from './components/ChatPanel';
import AdminControlPanel from './components/AdminControlPanel';
import HealthPanel from './components/HealthPanel';
import { AuthModal } from './components/auth/AuthModal';
import { useAuth } from './contexts/AuthContext';
import { MediaSoupClient } from './services/MediaSoupClient';
import { getAnalyticsManager } from './utils/analytics';
import './App.css';
const App = ({ config }) => {
    // Auth context
    const { isAuthenticated, logout, token, isLoading: authLoading } = useAuth();
    // State management
    const [localStream, setLocalStream] = useState(null);
    const [remoteStreams, setRemoteStreams] = useState({});
    const [cameraEnabled, setCameraEnabled] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('Connecting...');
    const [userCount, setUserCount] = useState(0);
    const [chatOpen, setChatOpen] = useState(true);
    const [isHost, setIsHost] = useState(false);
    const [adminPanelOpen, setAdminPanelOpen] = useState(false);
    const [healthPanelOpen, setHealthPanelOpen] = useState(false);
    const [currentUsername, setCurrentUsername] = useState('');
    const [showUsernames, setShowUsernames] = useState(false);
    const [usernames, setUsernames] = useState({});
    const [currentYouTubeVideo, setCurrentYouTubeVideo] = useState(null);
    const [currentMediaVideo, setCurrentMediaVideo] = useState(null);
    const [currentStream, setCurrentStream] = useState(null);
    const [isYouTubeProducer, setIsYouTubeProducer] = useState(false);
    const [featuredMode, setFeaturedMode] = useState(false);
    const [featuredModePreference, setFeaturedModePreference] = useState(false); // User's saved preference
    // YouTube playback timing state
    const [youtubeStartTime, setYoutubeStartTime] = useState(null);
    const [youtubeIsPaused, setYoutubeIsPaused] = useState(false);
    const [youtubePausedAt, setYoutubePausedAt] = useState(null);
    const [queueData, setQueueData] = useState([]);
    const [isDarkMode, setIsDarkMode] = useState(true);
    const [broadcastMode, setBroadcastMode] = useState(false);
    const [cameraRequired, setCameraRequired] = useState(false);
    // UPDATED: Load seasonal theme from localStorage on mount (defaults to true)
    const [seasonalTheme, setSeasonalTheme] = useState(() => {
        const saved = localStorage.getItem('gridchat-seasonal-theme');
        return saved !== null ? saved === 'true' : true;
    });
    const [blurredUsers, setBlurredUsers] = useState(new Map());
    const [socket, setSocket] = useState(null);
    const [calendarChannel, setCalendarChannel] = useState(null);
    const [bannedUsersChannel, setBannedUsersChannel] = useState(null);
    // Auth modal state
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [authModalMode, setAuthModalMode] = useState('login');
    const [requireLogin, setRequireLogin] = useState(false);
    // Global mute state
    const [isGloballyMuted, setIsGloballyMuted] = useState(false);
    // Refs
    const mediaSoupClient = useRef(null);
    const initializationStarted = useRef(false);
    const previousAuthState = useRef(isAuthenticated);
    const initialMount = useRef(true);
    // Configuration
    const finalConfig = {
        roomId: config?.roomId || 'default',
        mediaSoupUrl: 'wss://gridchat.net:3000',
        turnServers: []
    };
    // Apply theme class to body element for consistent background
    useEffect(() => {
        if (isDarkMode) {
            document.body.classList.add('dark-mode');
            document.body.classList.remove('light-mode');
        }
        else {
            document.body.classList.add('light-mode');
            document.body.classList.remove('dark-mode');
        }
    }, [isDarkMode]);
    // MediaSoup callbacks
    const handleStreamAdded = useCallback((stream, peerId, streamType) => {
        console.log('ðŸ“¹ Stream added in App:', peerId, streamType);
        const streamKey = `${peerId}-${streamType || 'camera'}`;
        setRemoteStreams(prev => {
            const existing = prev[streamKey];
            if (existing) {
                // consume() delivers audio and video as separate single-track MediaStreams
                // under the same key; replacing loses whichever arrived first.
                stream.getTracks().forEach(track => {
                    if (!existing.getTracks().some(t => t.id === track.id)) {
                        existing.addTrack(track);
                    }
                });
                return { ...prev, [streamKey]: existing };
            }
            return { ...prev, [streamKey]: stream };
        });
    }, []);
    const handleStreamRemoved = useCallback((peerId, streamType) => {
        console.log('ðŸ“¹ Stream removed in App:', peerId, streamType);
        const streamKey = `${peerId}-${streamType || 'camera'}`;
        setRemoteStreams(prev => {
            const updated = { ...prev };
            delete updated[streamKey];
            return updated;
        });
    }, []);
    const handleUserCountChanged = useCallback((count) => {
        console.log('ðŸ‘¥ User count changed in App:', count);
        setUserCount(count);
    }, []);
    const handleChatMessage = useCallback((message) => {
        console.log('ðŸ’¬ Chat message handled in App:', message);
    }, []);
    const handleQueueDataUpdate = useCallback((data) => {
        console.log('ðŸ“‹ Queue data updated:', data);
        setQueueData(data || []);
        // Forward to admin panel
        if (window.gridchatAdminPanel && !window.gridchatAdminPanel.closed) {
            window.gridchatAdminPanel.postMessage({
                type: 'queue-data-update',
                queueData: data || []
            }, '*');
        }
        // Forward to calendar via BroadcastChannel
        if (calendarChannel) {
            try {
                calendarChannel.postMessage({
                    type: 'queue-data-update',
                    data: { queueData: data || [] }
                });
            }
            catch (error) {
                console.warn('âš ï¸ Failed to post to calendar channel (may be closed):', error);
            }
        }
    }, [calendarChannel]);
    // Initialize BroadcastChannel for calendar communication
    useEffect(() => {
        if (finalConfig.roomId) {
            const channel = new BroadcastChannel(`gridchat-calendar-${finalConfig.roomId}`);
            // Listen for messages from calendar tab
            channel.onmessage = (event) => {
                const { type, data } = event.data;
                console.log('ðŸ“… Main app received from calendar:', type, data);
                switch (type) {
                    case 'request-queue-data':
                        channel.postMessage({
                            type: 'queue-data-update',
                            data: { queueData }
                        });
                        break;
                    case 'schedule-event':
                        if (socket) {
                            socket.emit('schedule-event', data);
                        }
                        break;
                    case 'move-scheduled-event':
                        if (socket) {
                            socket.emit('move-scheduled-event', data);
                        }
                        break;
                    case 'remove-scheduled-event':
                        if (socket) {
                            socket.emit('remove-scheduled-event', data);
                        }
                        break;
                    case 'update-broadcast-mode':
                        if (socket) {
                            socket.emit('update-broadcast-mode', data);
                        }
                        break;
                    case 'get-scheduled-events':
                        if (socket) {
                            socket.emit('get-scheduled-events', data);
                        }
                        break;
                    case 'get-server-files':
                        if (socket) {
                            socket.emit('get-server-files');
                        }
                        break;
                }
            };
            setCalendarChannel(channel);
            return () => {
                channel.close();
            };
        }
    }, [finalConfig.roomId, queueData, socket]);
    // Initialize BroadcastChannel for Banned Users Panel communication
    useEffect(() => {
        const channel = new BroadcastChannel('gridchat-banned-users');
        // Listen for messages from Banned Users Panel
        channel.onmessage = (event) => {
            const { type, data } = event.data;
            console.log('ðŸš« Main app received from banned users panel:', type, data);
            switch (type) {
                case 'request-ban-data':
                    // Send current ban data and connected users
                    if (socket) {
                        socket.emit('get-ban-data', {}, (response) => {
                            channel.postMessage({
                                type: 'banned-users-data',
                                data: response
                            });
                        });
                    }
                    break;
                case 'ban-user':
                    // Ban a user
                    if (socket && isHost) {
                        socket.emit('ban-user', data, (response) => {
                            if (response.success) {
                                channel.postMessage({
                                    type: 'ban-success',
                                    data: response
                                });
                            }
                            else {
                                channel.postMessage({
                                    type: 'ban-error',
                                    data: { error: response.error }
                                });
                            }
                        });
                    }
                    break;
                case 'unban-user':
                    // Unban a user
                    if (socket && isHost) {
                        socket.emit('unban-user', data, (response) => {
                            if (response.success) {
                                channel.postMessage({
                                    type: 'unban-success',
                                    data: response
                                });
                            }
                            else {
                                channel.postMessage({
                                    type: 'ban-error',
                                    data: { error: response.error }
                                });
                            }
                        });
                    }
                    break;
            }
        };
        setBannedUsersChannel(channel);
        return () => {
            channel.close();
        };
    }, [socket, isHost]);
    // Auto-manage featured mode based on video presence
    useEffect(() => {
        const hasVideo = currentYouTubeVideo || currentMediaVideo || currentStream;
        if (!hasVideo) {
            // No video playing - exit featured mode (show webcams in grid)
            if (featuredMode) {
                console.log('ðŸ“º No video playing - exiting featured mode');
                setFeaturedMode(false);
            }
        }
        else {
            // Video is playing - restore featured mode if preference is enabled
            if (featuredModePreference && !featuredMode) {
                console.log('ðŸ“º Video started - restoring featured mode from preference');
                setFeaturedMode(true);
            }
        }
    }, [currentYouTubeVideo, currentMediaVideo, currentStream, featuredModePreference, featuredMode]);
    // Socket event listeners
    const setupSocketEventListeners = useCallback((socket) => {
        console.log('ðŸ”— Setting up socket event listeners...');
        const handleUsernamesUpdate = (data) => {
            console.log('ðŸ‘¤ Room usernames updated:', data);
            setUsernames(data);
        };
        const handleChatMessageReceived = (message) => {
            console.log('ðŸ’¬ Chat message received via MediaSoup:', message);
        };
        const handleHostStatusUpdate = (data) => {
            console.log('ðŸ‘‘ Host status updated:', data.isHost);
            setIsHost(data.isHost);
            // Admin panel now only opens via /panel command (not automatically)
            if (data.isHost) {
                console.log('ðŸ‘‘ Host status confirmed - admin panel available via /panel command');
            }
        };
        const handleAdminPanelAutoOpen = () => {
            console.log('ðŸŽ›ï¸ Admin panel toggle triggered');
            setAdminPanelOpen(prev => !prev);
        };
        const handleHealthPanelToggle = () => {
            console.log('ðŸ¥ Health panel toggle triggered');
            setHealthPanelOpen(prev => !prev);
        };
        const handleCalendarPanelToggle = () => {
            console.log('ðŸ“… Opening calendar in new tab');
            const calendarUrl = `/calendar?roomId=${finalConfig.roomId}`;
            window.open(calendarUrl, '_blank');
        };
        const handleAnalyticsPanelToggle = () => {
            console.log('ðŸ“Š Opening analytics in new tab');
            const analyticsUrl = `/analytics?roomId=${finalConfig.roomId}`;
            window.open(analyticsUrl, '_blank');
        };
        const handleOpenLoginModal = () => {
            console.log('ðŸ” Opening login modal via /login command');
            setAuthModalMode('login');
            setAuthModalOpen(true);
        };
        const handleOpenRegisterModal = () => {
            console.log('âœï¸ Opening register modal via /register command');
            setAuthModalMode('register');
            setAuthModalOpen(true);
        };
        const handleLogoutUser = () => {
            console.log('ðŸšª Logging out via /logout command');
            logout();
            // Reload page to fully clear session and reconnect
            window.location.reload();
        };
        const handleRequireLoginUpdated = (data) => {
            console.log('ðŸ” Login requirement updated:', data.requireLogin);
            setRequireLogin(data.requireLogin);
        };
        const handleYouTubeVideoUpdate = (data) => {
            console.log('ðŸŽ¬ YouTube video updated:', data);
            // STATE PRESERVATION: Include seekTo in video object if provided
            // CRITICAL: Check if currentVideo key exists in data object
            // If it exists (even if null), use it; otherwise try legacy format
            const videoData = ('currentVideo' in data)
                ? data.currentVideo // This can be null to clear the video
                : (data.id ? data : null);
            if (videoData && data.seekTo !== undefined) {
                videoData.seekTo = data.seekTo;
                console.log(`â© YouTube video should seek to ${data.seekTo}s`);
            }
            setCurrentYouTubeVideo(videoData);
            setIsYouTubeProducer(data.isProducer || false);
            // Update YouTube timing state when video changes
            if (data.startTime !== undefined)
                setYoutubeStartTime(data.startTime);
            if (data.isPaused !== undefined)
                setYoutubeIsPaused(data.isPaused);
            if (data.pausedAt !== undefined)
                setYoutubePausedAt(data.pausedAt);
            if (data.isNewUserSync && data.currentVideo) {
                console.log('ðŸ”„ New user sync detected - triggering auto-play for YouTube video:', data.currentVideo.videoId);
            }
        };
        const handleMediaVideoUpdate = (data) => {
            console.log('ðŸŽµ Media video updated:', data);
            // STATE PRESERVATION: Include seekTo in video object if provided
            const mediaData = data.currentMediaVideo || data.currentFile || (data.filename ? data : null);
            if (mediaData && data.seekTo !== undefined) {
                mediaData.seekTo = data.seekTo;
                console.log(`â© Media video should seek to ${data.seekTo}s`);
            }
            setCurrentMediaVideo(mediaData);
        };
        const handleStreamUpdate = (data) => {
            console.log('ðŸ“¡ Stream updated:', data);
            const stream = data.action === 'stop' ? null : (data.currentStream || null);
            setCurrentStream(stream);
            // Forward to admin panel
            if (window.gridchatAdminPanel && !window.gridchatAdminPanel.closed) {
                window.gridchatAdminPanel.postMessage({
                    type: 'stream-updated',
                    currentStream: stream
                }, '*');
            }
        };
        const handleUserBlurUpdate = (data) => {
            console.log('ðŸ‘ï¸ User blur updated:', data);
            console.log('ðŸ” Blur details - socketId:', data.socketId, 'username:', data.username, 'isBlurred:', data.isBlurred, 'blurAmount:', data.blurAmount);
            console.log('ðŸ” Current socket.id:', socket?.id);
            setBlurredUsers(prev => {
                const newMap = new Map(prev);
                if (data.isBlurred && data.blurAmount > 0) {
                    newMap.set(data.socketId, data.blurAmount);
                    console.log('âœ… Set blur for user:', data.socketId, 'at', data.blurAmount + '%');
                }
                else {
                    newMap.delete(data.socketId);
                    console.log('âŒ Removed blur for user:', data.socketId);
                }
                console.log('ðŸ“‹ Current blurredUsers Map:', Array.from(newMap.entries()));
                return newMap;
            });
        };
        const handleFeaturedModeUpdate = (data) => {
            console.log('ðŸŽ­ Featured mode updated:', data.featuredMode);
            setFeaturedMode(data.featuredMode);
            setFeaturedModePreference(data.featuredMode); // Save user's preference
            if (window.gridchatAdminPanel && !window.gridchatAdminPanel.closed) {
                window.gridchatAdminPanel.postMessage({
                    type: 'featured-mode-state-update',
                    featuredMode: data.featuredMode
                }, '*');
            }
        };
        const handleScheduledEventsUpdated = (data) => {
            console.log('ðŸ“… Scheduled events updated:', data.events);
            // Forward to calendar via BroadcastChannel (NOT admin panel)
            if (calendarChannel) {
                try {
                    calendarChannel.postMessage({
                        type: 'scheduled-events-updated',
                        data: data
                    });
                }
                catch (error) {
                    console.warn('âš ï¸ Failed to post to calendar channel (may be closed):', error);
                }
            }
        };
        const handleBroadcastModeUpdated = (data) => {
            console.log('ðŸ“¡ Broadcast mode updated:', data);
            // Update local broadcast mode state
            setBroadcastMode(data.broadcastMode);
            // Forward to calendar via BroadcastChannel
            if (calendarChannel) {
                try {
                    calendarChannel.postMessage({
                        type: 'broadcast-mode-updated',
                        data: data
                    });
                }
                catch (error) {
                    console.warn('âš ï¸ Failed to post to calendar channel (may be closed):', error);
                }
            }
        };
        const handleBroadcastVideoUpdated = (data) => {
            console.log('ðŸ“º Broadcast video updated:', data);
            if (data.currentBroadcastVideo) {
                const content = data.currentBroadcastVideo;
                // Check if this is a YouTube broadcast video
                if (content.type === 'youtube' && content.videoId) {
                    console.log('ðŸ“º YouTube broadcast video:', content.title);
                    // Set as YouTube video (will use YouTube player)
                    setCurrentYouTubeVideo({
                        videoId: content.videoId,
                        title: content.title,
                        isBroadcast: true
                    });
                    // Clear media video
                    setCurrentMediaVideo(null);
                }
                else {
                    // Local broadcast video - set as media video
                    console.log('ðŸ“º Local broadcast video:', content.title);
                    setCurrentMediaVideo({
                        ...content,
                        isBroadcast: true // Mark as broadcast video
                    });
                    // Clear YouTube video
                    setCurrentYouTubeVideo(null);
                }
            }
            else {
                // Broadcast stopped, clear both
                setCurrentMediaVideo(null);
                setCurrentYouTubeVideo(null);
            }
        };
        const handleServerFilesUpdated = (data) => {
            console.log('ðŸ“ Server files updated:', data);
            // Forward to calendar via BroadcastChannel
            if (calendarChannel) {
                try {
                    calendarChannel.postMessage({
                        type: 'server-files-updated',
                        data: data
                    });
                }
                catch (error) {
                    console.warn('âš ï¸ Failed to post to calendar channel (may be closed):', error);
                }
            }
        };
        const handleCameraWarning = (data) => {
            console.log('ðŸ“¹ Camera warning received:', data);
            // Show alert to user
            alert(data.message || 'This room requires cameras to be enabled. Please turn on your camera.');
        };
        const handleKicked = (data) => {
            console.log('ðŸš« User kicked:', data);
            // Show modal or alert
            alert(data.reason || 'You have been disconnected from the room.');
            // Optionally reload the page or redirect
            // window.location.reload();
        };
        const handleCameraRequiredUpdated = (data) => {
            console.log('ðŸ“¹ Camera requirement updated:', data.cameraRequired);
            // Update local state
            setCameraRequired(data.cameraRequired);
            // Forward to admin panel if open
            if (window.gridchatAdminPanel && !window.gridchatAdminPanel.closed) {
                window.gridchatAdminPanel.postMessage({
                    type: 'camera-required-update',
                    cameraRequired: data.cameraRequired
                }, '*');
            }
            // You could show a notification to users
            if (data.cameraRequired) {
                console.log('ðŸ“¹ Camera is now required in this room');
            }
            else {
                console.log('ðŸ“¹ Camera is no longer required in this room');
            }
        };
        const handleUsernameVisibilityUpdated = (data) => {
            console.log('ðŸ‘¤ Username visibility updated:', data.showUsernames);
            // Update local state
            setShowUsernames(data.showUsernames);
            // Forward to admin panel if open
            if (window.gridchatAdminPanel && !window.gridchatAdminPanel.closed) {
                window.gridchatAdminPanel.postMessage({
                    type: 'username-display-update',
                    data: { showUsernames: data.showUsernames }
                }, '*');
            }
        };
        // Register all socket listeners
        console.log(`ðŸ”§ Registering socket event listeners for socket ${socket.id}...`);
        socket.on('usernames-updated', handleUsernamesUpdate);
        socket.on('chat-message', handleChatMessageReceived);
        socket.on('host-status', handleHostStatusUpdate);
        socket.on('admin-panel-auto-open', handleAdminPanelAutoOpen);
        socket.on('health-panel-toggle', handleHealthPanelToggle);
        socket.on('calendar-panel-toggle', handleCalendarPanelToggle);
        socket.on('analytics-panel-toggle', handleAnalyticsPanelToggle);
        socket.on('open-login-modal', handleOpenLoginModal);
        socket.on('open-register-modal', handleOpenRegisterModal);
        socket.on('logout-user', handleLogoutUser);
        socket.on('require-login-updated', handleRequireLoginUpdated);
        socket.on('youtube-video-updated', handleYouTubeVideoUpdate);
        socket.on('media-video-updated', handleMediaVideoUpdate);
        socket.on('stream-updated', handleStreamUpdate);
        socket.on('user-blur-updated', handleUserBlurUpdate);
        socket.on('featured-mode-updated', handleFeaturedModeUpdate);
        socket.on('queue-data-updated', handleQueueDataUpdate);
        socket.on('scheduled-events-updated', handleScheduledEventsUpdated);
        socket.on('broadcast-mode-updated', handleBroadcastModeUpdated);
        socket.on('broadcast-video-updated', handleBroadcastVideoUpdated);
        socket.on('server-files-updated', handleServerFilesUpdated);
        socket.on('camera-warning', handleCameraWarning);
        socket.on('kicked', handleKicked);
        socket.on('camera-required-updated', handleCameraRequiredUpdated);
        socket.on('username-visibility-updated', handleUsernameVisibilityUpdated);
        console.log(`âœ… Socket event listeners registered successfully for socket ${socket.id}`);
        return () => {
            socket.off('usernames-updated', handleUsernamesUpdate);
            socket.off('chat-message', handleChatMessageReceived);
            socket.off('host-status', handleHostStatusUpdate);
            socket.off('admin-panel-auto-open', handleAdminPanelAutoOpen);
            socket.off('health-panel-toggle', handleHealthPanelToggle);
            socket.off('calendar-panel-toggle', handleCalendarPanelToggle);
            socket.off('analytics-panel-toggle', handleAnalyticsPanelToggle);
            socket.off('open-login-modal', handleOpenLoginModal);
            socket.off('open-register-modal', handleOpenRegisterModal);
            socket.off('logout-user', handleLogoutUser);
            socket.off('require-login-updated', handleRequireLoginUpdated);
            socket.off('youtube-video-updated', handleYouTubeVideoUpdate);
            socket.off('media-video-updated', handleMediaVideoUpdate);
            socket.off('stream-updated', handleStreamUpdate);
            socket.off('featured-mode-updated', handleFeaturedModeUpdate);
            socket.off('queue-data-updated', handleQueueDataUpdate);
            socket.off('scheduled-events-updated', handleScheduledEventsUpdated);
            socket.off('broadcast-mode-updated', handleBroadcastModeUpdated);
            socket.off('broadcast-video-updated', handleBroadcastVideoUpdated);
            socket.off('server-files-updated', handleServerFilesUpdated);
            socket.off('camera-warning', handleCameraWarning);
            socket.off('kicked', handleKicked);
            socket.off('camera-required-updated', handleCameraRequiredUpdated);
            socket.off('username-visibility-updated', handleUsernameVisibilityUpdated);
        };
    }, [handleQueueDataUpdate, finalConfig.roomId, calendarChannel, logout]);
    // Check if login is required on page load
    useEffect(() => {
        if (requireLogin && !isAuthenticated) {
            console.log('ðŸ” Login required - showing auth modal');
            setAuthModalOpen(true);
        }
    }, [requireLogin, isAuthenticated]);
    // Save seasonal theme to localStorage when it changes
    useEffect(() => {
        console.log('ðŸŽƒ Saving seasonal theme to localStorage:', seasonalTheme);
        localStorage.setItem('gridchat-seasonal-theme', seasonalTheme.toString());
    }, [seasonalTheme]);
    // Update favicon based on mute state
    useEffect(() => {
        const updateFavicon = (isMuted) => {
            // Create an SVG favicon with speaker icon
            const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="48" fill="${isMuted ? '#ef4444' : '#8b5cf6'}" stroke="white" stroke-width="4"/>
          ${isMuted
                ? `<!-- Muted speaker with X -->
               <path d="M30 35 L30 65 L45 65 L60 75 L60 25 L45 35 Z" fill="white"/>
               <line x1="65" y1="35" x2="85" y2="65" stroke="white" stroke-width="6" stroke-linecap="round"/>
               <line x1="85" y1="35" x2="65" y2="65" stroke="white" stroke-width="6" stroke-linecap="round"/>`
                : `<!-- Unmuted speaker with sound waves -->
               <path d="M30 35 L30 65 L45 65 L60 75 L60 25 L45 35 Z" fill="white"/>
               <path d="M70 35 Q 75 40, 75 50 Q 75 60, 70 65" stroke="white" stroke-width="4" fill="none" stroke-linecap="round"/>
               <path d="M78 30 Q 85 38, 85 50 Q 85 62, 78 70" stroke="white" stroke-width="4" fill="none" stroke-linecap="round"/>`}
        </svg>
      `;
            // Convert SVG to data URI
            const blob = new Blob([svg], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(blob);
            // Update favicon
            let link = document.querySelector("link[rel*='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.head.appendChild(link);
            }
            link.href = url;
            console.log(`ðŸŽ¨ Updated favicon: ${isMuted ? 'muted' : 'unmuted'}`);
        };
        updateFavicon(isGloballyMuted);
    }, [isGloballyMuted]);
    const requestQueueData = useCallback(() => {
        if (socket) {
            console.log('ðŸ“‹ Requesting queue data from server');
            socket.emit('get-queue-data');
        }
    }, [socket]);
    const initializeMediaSoup = useCallback(async () => {
        if (initializationStarted.current || mediaSoupClient.current) {
            console.log('âš ï¸ MediaSoup initialization already started or completed');
            return;
        }
        try {
            initializationStarted.current = true;
            console.log('ðŸš€ Starting MediaSoup client initialization...');
            mediaSoupClient.current = new MediaSoupClient(handleStreamAdded, handleStreamRemoved, handleUserCountChanged, handleChatMessage, token);
            // IMPORTANT: Set up socket listeners BEFORE joining room
            // This ensures we catch the host-status event that's emitted during join
            if (mediaSoupClient.current.socket) {
                console.log('ðŸ”— Setting up socket event listeners BEFORE joining room...');
                setupSocketEventListeners(mediaSoupClient.current.socket);
                window.gridchatSocket = mediaSoupClient.current.socket;
                setSocket(mediaSoupClient.current.socket);
                console.log('âœ… Socket event listeners setup completed');
            }
            setConnectionStatus('Joining room...');
            console.log('ðŸ  Joining room via MediaSoup client...');
            const result = await mediaSoupClient.current.joinRoom(finalConfig.roomId);
            console.log('ðŸ  Room join result:', result);
            if (result.requireLogin !== undefined) {
                setRequireLogin(result.requireLogin);
            }
            // Set current YouTube video from room state if it exists
            if (result.currentYouTubeVideo) {
                console.log('ðŸŽ¬ Setting current YouTube video from room state:', result.currentYouTubeVideo);
                console.log('â±ï¸ YouTube timing state:', {
                    startTime: result.youtubeStartTime,
                    isPaused: result.youtubeIsPaused,
                    pausedAt: result.youtubePausedAt
                });
                // STATE PRESERVATION: Preserve seekTo if it was already set by youtube-video-updated event
                setCurrentYouTubeVideo(prev => {
                    const videoData = { ...result.currentYouTubeVideo };
                    if (prev && prev.seekTo !== undefined) {
                        videoData.seekTo = prev.seekTo;
                        console.log(`â© Preserving seekTo: ${prev.seekTo}s from previous state`);
                    }
                    return videoData;
                });
                setIsYouTubeProducer(result.youtubeStartTime !== undefined && result.youtubeStartTime !== null);
                // Set YouTube timing state for resume functionality
                setYoutubeStartTime(result.youtubeStartTime || null);
                setYoutubeIsPaused(result.youtubeIsPaused || false);
                setYoutubePausedAt(result.youtubePausedAt || null);
            }
            // Set current media video from room state if it exists
            if (result.currentMediaVideo) {
                console.log('ðŸŽµ Setting current media video from room state:', result.currentMediaVideo);
                setCurrentMediaVideo(result.currentMediaVideo);
            }
            // Set current stream from room state if it exists
            if (result.currentStream) {
                console.log('ðŸ“¡ Setting current stream from room state:', result.currentStream);
                setCurrentStream(result.currentStream);
            }
            // Set current broadcast video from room state if it exists
            if (result.currentBroadcastVideo) {
                console.log('ðŸ“º Setting current broadcast video from room state:', result.currentBroadcastVideo);
                const content = result.currentBroadcastVideo;
                // Check if this is a YouTube broadcast video
                if (content.type === 'youtube' && content.videoId) {
                    console.log('ðŸ“º YouTube broadcast video:', content.title);
                    setCurrentYouTubeVideo({
                        id: content.videoId,
                        videoId: content.videoId,
                        title: content.title,
                        thumbnail: content.thumbnail || '',
                        duration: content.duration || 0,
                        url: `https://youtube.com/watch?v=${content.videoId}`,
                        addedBy: 'broadcast',
                        addedAt: new Date(),
                        isBroadcast: true
                    });
                }
                else if (content.type === 'local' && content.filename) {
                    // Local broadcast video
                    console.log('ðŸ“º Local broadcast video:', content.title);
                    setCurrentMediaVideo({
                        filename: content.filename,
                        title: content.title,
                        path: content.path,
                        addedBy: 'broadcast',
                        addedAt: new Date(),
                        duration: content.duration || 0,
                        isBroadcast: true
                    });
                }
            }
            // Set featured mode from room state
            if (result.featuredMode !== undefined) {
                console.log('ðŸŽ­ Setting featured mode from room state:', result.featuredMode);
                setFeaturedMode(result.featuredMode);
                setFeaturedModePreference(result.featuredMode); // Save initial preference
            }
            // Set showUsernames from room state
            if (result.showUsernames !== undefined) {
                console.log('ðŸ‘¤ Setting showUsernames from room state:', result.showUsernames);
                setShowUsernames(result.showUsernames);
            }
            setIsConnected(true);
            setConnectionStatus('Connected');
            console.log('âœ… MediaSoup initialization completed');
            // Initialize analytics
            if (mediaSoupClient.current?.socket) {
                console.log('ðŸ“Š Initializing analytics manager...');
                const analyticsManager = getAnalyticsManager();
                await analyticsManager.initialize(mediaSoupClient.current.socket);
                // Sync current webcam state with analytics
                if (cameraEnabled) {
                    console.log('ðŸ“· Syncing initial webcam state (ON) with analytics');
                    analyticsManager.setWebcamStatus(true);
                }
            }
            // Mark that initial mount is complete
            initialMount.current = false;
            if (!mediaSoupClient.current) {
                console.log('âš ï¸ MediaSoup client was cleaned up during initialization');
                return;
            }
        }
        catch (error) {
            console.error('âŒ MediaSoup initialization failed:', error);
            setConnectionStatus('Connection failed');
            initializationStarted.current = false;
        }
    }, [finalConfig.roomId, handleStreamAdded, handleStreamRemoved, handleUserCountChanged, handleChatMessage, setupSocketEventListeners, token]);
    useEffect(() => {
        // Wait for auth to finish loading before initializing
        if (authLoading) {
            console.log('â³ Waiting for auth to finish loading...');
            return;
        }
        console.log('âœ… Auth loaded, initializing MediaSoup...');
        initializeMediaSoup();
        return () => {
            console.log('ðŸ§¹ App unmounting - cleaning up MediaSoup client');
            setTimeout(() => {
                if (mediaSoupClient.current) {
                    console.log('ðŸ§¹ Disconnecting MediaSoup client...');
                    mediaSoupClient.current.disconnect();
                    mediaSoupClient.current = null;
                    initializationStarted.current = false;
                }
            }, 1000);
        };
    }, [authLoading]);
    // Reconnect socket when authentication state changes (login during session)
    useEffect(() => {
        const reconnectAfterAuth = async () => {
            // Wait for auth to finish loading
            if (authLoading) {
                return;
            }
            // Skip if this is the initial mount - we don't want to reconnect on page load
            if (initialMount.current) {
                previousAuthState.current = isAuthenticated;
                return;
            }
            // Skip if mediaSoupClient doesn't exist yet
            // Only reconnect if user logs in DURING an active session
            if (!mediaSoupClient.current) {
                previousAuthState.current = isAuthenticated;
                return;
            }
            // Only reconnect if authentication changed from false to true AFTER initial mount
            const authChanged = !previousAuthState.current && isAuthenticated;
            if (authChanged && token) {
                console.log('ðŸ” User logged in during session - reconnecting socket with token...');
                // Store camera state to restore after reconnect
                const wasCameraEnabled = cameraEnabled;
                // Disconnect old client
                console.log('ðŸ”Œ Disconnecting old anonymous socket...');
                mediaSoupClient.current.disconnect();
                mediaSoupClient.current = null;
                initializationStarted.current = false;
                // Clear states
                setSocket(null);
                setIsConnected(false);
                setLocalStream(null);
                setCameraEnabled(false);
                // Wait a moment for cleanup
                await new Promise(resolve => setTimeout(resolve, 500));
                // Reinitialize with authenticated token
                console.log('ðŸ”„ Reinitializing with authenticated token...');
                await initializeMediaSoup();
                // Restore camera if it was enabled
                if (wasCameraEnabled && mediaSoupClient.current) {
                    console.log('ðŸ“· Restoring camera after reconnection...');
                    setTimeout(async () => {
                        try {
                            if (mediaSoupClient.current) {
                                const stream = await mediaSoupClient.current.enableCamera();
                                setCameraEnabled(true);
                                setLocalStream(stream);
                                console.log('âœ… Camera restored after reconnection');
                            }
                        }
                        catch (error) {
                            console.error('âŒ Failed to restore camera:', error);
                        }
                    }, 1000);
                }
            }
            // Update previous auth state
            previousAuthState.current = isAuthenticated;
        };
        reconnectAfterAuth();
    }, [isAuthenticated, token, authLoading]);
    // Admin panel message handler
    useEffect(() => {
        const handleAdminPanelMessage = (event) => {
            if (event.origin !== window.location.origin)
                return;
            console.log('ðŸ“¨ Received admin panel message:', event.data.type, event.data.action, event.data.data);
            if (!socket)
                return;
            const { type, action, data } = event.data;
            switch (type) {
                case 'request-queue-data':
                    requestQueueData();
                    // Also send username display state to admin panel
                    if (window.gridchatAdminPanel && !window.gridchatAdminPanel.closed) {
                        window.gridchatAdminPanel.postMessage({
                            type: 'username-display-update',
                            data: { showUsernames }
                        }, '*');
                    }
                    break;
                case 'request-stream-data':
                    // Send current stream info to admin panel
                    if (window.gridchatAdminPanel && !window.gridchatAdminPanel.closed) {
                        window.gridchatAdminPanel.postMessage({
                            type: 'stream-updated',
                            currentStream: currentStream
                        }, '*');
                    }
                    break;
                case 'admin-panel-closed':
                    break;
                case 'media-control':
                    socket.emit('media-control', { action, filename: data?.filename });
                    break;
                case 'youtube-play':
                    socket.emit('youtube-control', { action: 'play' });
                    break;
                case 'youtube-pause':
                    socket.emit('youtube-control', { action: 'pause' });
                    break;
                case 'youtube-stop':
                    socket.emit('youtube-control', { action: 'stop' });
                    break;
                case 'youtube-skip':
                    if (socket && isHost) {
                        socket.emit('chat-command', { command: 'skip', args: [] });
                    }
                    break;
                case 'stream-play':
                    if (socket && isHost && data?.streamId) {
                        socket.emit('stream-control', { streamId: data.streamId, action: 'play' });
                    }
                    break;
                case 'stream-pause':
                    if (socket && isHost && data?.streamId) {
                        socket.emit('stream-control', { streamId: data.streamId, action: 'pause' });
                    }
                    break;
                case 'stream-stop':
                    if (socket && isHost && data?.streamId) {
                        socket.emit('chat-command', { command: 'stopstream', args: [] });
                    }
                    break;
                case 'media-play':
                    if (data?.filename) {
                        const videoElement = document.getElementById(`media-video-${data.filename}`);
                        if (videoElement)
                            videoElement.play();
                    }
                    break;
                case 'media-pause':
                    if (data?.filename) {
                        const videoElement = document.getElementById(`media-video-${data.filename}`);
                        if (videoElement)
                            videoElement.pause();
                    }
                    break;
                case 'media-stop':
                    if (socket && isHost) {
                        socket.emit('chat-command', { command: 'stopmedia', args: [] });
                    }
                    break;
                case 'toggle-usernames':
                    // Send to server to sync with all users
                    if (socket && isHost) {
                        socket.emit('toggle-usernames');
                    }
                    break;
                case 'toggle-featured-mode':
                    socket.emit('toggle-featured-mode');
                    break;
                case 'clear-queue':
                    socket.emit('clear-queue');
                    break;
                case 'shuffle-queue':
                    socket.emit('shuffle-queue');
                    break;
                case 'remove-queue-item':
                    socket.emit('remove-queue-item', { index: data?.index });
                    break;
                case 'move-queue-item':
                    socket.emit('move-queue-item', { fromIndex: data?.fromIndex, toIndex: data?.toIndex });
                    break;
                case 'toggle-seasonal-theme':
                    if (data?.enabled !== undefined) {
                        setSeasonalTheme(data.enabled);
                    }
                    break;
                case 'chat-command':
                    if (socket) {
                        socket.emit('chat-command', { command: data?.command, args: data?.args || [] });
                    }
                    break;
                case 'update-camera-required':
                    if (socket && isHost) {
                        const { cameraRequired } = event.data;
                        socket.emit('update-camera-required', { cameraRequired });
                        console.log('ðŸ“¹ Updating camera requirement to:', cameraRequired);
                    }
                    break;
                case 'request-camera-required':
                    // Admin panel requesting current camera requirement status
                    if (window.gridchatAdminPanel && !window.gridchatAdminPanel.closed) {
                        window.gridchatAdminPanel.postMessage({
                            type: 'camera-required-update',
                            cameraRequired: cameraRequired
                        }, '*');
                        console.log('ðŸ“¹ Sent camera requirement status to admin panel:', cameraRequired);
                    }
                    break;
                default:
                    console.log('Unknown admin panel message type:', type);
            }
        };
        window.addEventListener('message', handleAdminPanelMessage);
        return () => window.removeEventListener('message', handleAdminPanelMessage);
    }, [requestQueueData, socket, isHost, queueData, currentStream, cameraRequired]);
    useEffect(() => {
        window.gridchatState = {
            currentYouTubeVideo,
            currentMediaVideo,
            userCount,
            featuredMode,
            queueData,
            remoteStreams,
            localStreamActive: cameraEnabled
        };
    }, [currentYouTubeVideo, currentMediaVideo, userCount, featuredMode, queueData, remoteStreams, cameraEnabled]);
    const handleCameraToggle = async () => {
        if (!mediaSoupClient.current)
            return;
        try {
            if (cameraEnabled) {
                await mediaSoupClient.current.disableCamera();
                setCameraEnabled(false);
                setLocalStream(null);
                console.log('ðŸ“· Camera disabled, local stream cleared');
                // Track webcam status in analytics
                const analyticsManager = getAnalyticsManager();
                analyticsManager.setWebcamStatus(false);
            }
            else {
                const stream = await mediaSoupClient.current.enableCamera();
                setCameraEnabled(true);
                setLocalStream(stream);
                console.log('ðŸ“· Camera enabled, local stream set:', stream);
                // Track webcam status in analytics
                const analyticsManager = getAnalyticsManager();
                analyticsManager.setWebcamStatus(true);
            }
        }
        catch (error) {
            console.error('Camera toggle failed:', error);
        }
    };
    const handleUsernameSet = (username) => {
        setCurrentUsername(username);
    };
    const handleCloseAdminPanel = () => {
        setAdminPanelOpen(false);
    };
    const handleYouTubeControl = (action) => {
        if (socket) {
            console.log('ðŸŽ¬ YouTube control action from featured click:', action);
            if (action === 'play-pause') {
                console.log('ðŸŽ¬ Featured mode click - YouTubeTile will handle via its own click handler');
            }
            else {
                socket.emit('youtube-control', { action });
            }
        }
    };
    const renderFeaturedVideo = () => {
        if (!featuredMode)
            return null;
        return (_jsx("div", { className: "featured-video-container" }));
    };
    const canCloseAuthModal = !requireLogin || isAuthenticated;
    return (_jsxs("div", { className: `app ${isDarkMode ? 'dark-mode' : 'light-mode'} ${seasonalTheme ? 'seasonal-theme' : ''}`, children: [_jsx(AuthModal, { isOpen: authModalOpen, onClose: () => canCloseAuthModal && setAuthModalOpen(false), initialMode: authModalMode }), _jsxs("div", { className: "main-content", style: {
                    filter: authModalOpen ? 'blur(8px)' : 'none',
                    pointerEvents: authModalOpen ? 'none' : 'auto'
                }, children: [renderFeaturedVideo(), _jsx("div", { className: "video-section", style: {
                            display: featuredMode ? 'block' : 'block',
                            marginTop: featuredMode ? '20px' : '0'
                        }, children: _jsx(VideoGrid, { localStream: localStream, remoteStreams: remoteStreams, cameraEnabled: cameraEnabled, usernames: usernames, currentUsername: currentUsername, showUsernames: showUsernames, currentYouTubeVideo: currentYouTubeVideo, currentMediaVideo: currentMediaVideo, isYouTubeProducer: isYouTubeProducer, isHost: isHost, socket: socket, onYouTubeControl: handleYouTubeControl, featuredMode: featuredMode, blurredUsers: blurredUsers, youtubeStartTime: youtubeStartTime, youtubeIsPaused: youtubeIsPaused, youtubePausedAt: youtubePausedAt, broadcastMode: broadcastMode, isGloballyMuted: isGloballyMuted, isAuthenticated: isAuthenticated }) }), _jsx("button", { onClick: handleCameraToggle, className: `camera-btn ${cameraEnabled ? 'camera-active' : ''}`, disabled: !isConnected, children: "Cam" })] }), _jsx(ChatPanel, { isOpen: chatOpen, onClose: () => setChatOpen(!chatOpen), socket: socket, currentUsername: currentUsername, onUsernameSet: handleUsernameSet, isDarkMode: isDarkMode, onThemeChange: setIsDarkMode }), adminPanelOpen && (_jsx(AdminControlPanel, { isOpen: true, onClose: handleCloseAdminPanel, currentYouTubeVideo: currentYouTubeVideo, currentMediaVideo: currentMediaVideo, userCount: userCount, isHost: isHost, queueData: queueData, featuredMode: featuredMode })), healthPanelOpen && (_jsx(HealthPanel, { isOpen: healthPanelOpen, onClose: () => setHealthPanelOpen(false) }))] }));
};
export default App;
